import { useHasOpsPilotQuestionsInLocalStorage } from '@cast/features-organization-shared-opspilot';
import { useOrganizationSettingsQuery } from '@cast/infrastructure-abilities';
import { useOrganizations } from '@cast/shared-organization-context-data-access';
import { useReportingMonetizationReadinessQuery } from '@cast/shared-trial-data-access';
import { useCanFetchReportingMonetization } from '@cast/shared-trial-ui';

import { buildOnboardingTasks } from './_utils';
import { isOpsPilotEnabledForOnboarding } from './isOpsPilotEnabledForOnboarding';
import type { OnboardingTask } from './types';

/**
 * Org-level Cast Radar activation checklist.
 * Progress comes from GET …/reporting-monetization/readiness (shared across the org).
 * Ask OpsPilot also ORs localStorage chat history while PostHog readiness lags.
 * Ask OpsPilot is only included when Feature Control has OpsPilot enabled.
 */
export const useOnboardingWizardTasks = (): {
  tasks: OnboardingTask[];
  isLoading: boolean;
} => {
  const { canFetch, isAbilityLoading } = useCanFetchReportingMonetization();
  const { data, isLoading: isReadinessLoading } =
    useReportingMonetizationReadinessQuery({
      enabled: canFetch,
    });
  const { currentOrganization } = useOrganizations();
  const hasAskedOpsPilotQuestionLocally = useHasOpsPilotQuestionsInLocalStorage(
    currentOrganization?.id
  );
  const { data: organizationSettings } = useOrganizationSettingsQuery({
    organizationId: currentOrganization?.id,
    enabled: !!currentOrganization?.id,
  });

  const copilotSettings = organizationSettings?.copilotSettings;

  return {
    tasks: buildOnboardingTasks(data, {
      isOpsPilotEnabled: isOpsPilotEnabledForOnboarding({
        organizationInitialized: copilotSettings?.organizationInitialized,
        enabled: copilotSettings?.enabled,
      }),
      hasAskedOpsPilotQuestionLocally,
    }),
    // Don't wait on org settings — until they load, OpsPilot is omitted and
    // other tasks still drive FAB visibility.
    isLoading: isAbilityLoading || (canFetch && isReadinessLoading),
  };
};
